import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiArrowLeft, FiList, FiGrid } from 'react-icons/fi';
import config from '../config';
import TechPill from '../components/TechPill';
import '../styles/Archive.css';

function getLinkDisplay(project) {
  const url = project.paperLink || project.external;
  if (!url) return null;
  try {
    const hostname = new URL(url).hostname.replace('www.', '');
    return { href: url, label: hostname };
  } catch {
    return null;
  }
}

function Archive() {
  const [view, setView] = useState('list');
  const sortedProjects = [...config.projects].sort(
    (a, b) => Number(b.year) - Number(a.year)
  );

  return (
    <div className="archive-page">
      <Helmet>
        <title>Devang Saraogi — Project Archive</title>
        <meta name="description" content="All projects by Devang Saraogi — ML, AI, distributed systems, and software engineering projects." />
        <link rel="canonical" href="https://devangsaraogi.github.io/archive" />
      </Helmet>
      <div className="archive-top-row">
        <div>
          <Link to="/" className="archive-back-link">
            <FiArrowLeft className="title-arrow title-arrow-left" /> {config.personalInfo.name}
          </Link>
          <h1 className="archive-title">All Projects</h1>
        </div>
        <div className="archive-view-toggle">
          <button
            className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <FiList />
          </button>
          <button
            className={`view-toggle-btn ${view === 'gallery' ? 'active' : ''}`}
            onClick={() => setView('gallery')}
            aria-label="Gallery view"
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <div className="archive-table" role="table">
          <div className="archive-header" role="row">
            <span role="columnheader">Year</span>
            <span role="columnheader">Project</span>
            <span role="columnheader" className="archive-col-tech">Built with</span>
            <span role="columnheader">Link</span>
          </div>

          {sortedProjects.map((project, index) => {
            const link = getLinkDisplay(project);
            return (
              <div className="archive-row" role="row" key={index}>
                <span className="archive-year" role="cell">{project.year}</span>
                <span className="archive-project" role="cell">
                  {(project.external || project.paperLink) ? (
                    <a href={project.paperLink || project.external} target="_blank" rel="noopener noreferrer" className="archive-project-title">
                      {project.title} <FiArrowUpRight className="title-arrow" />
                    </a>
                  ) : (
                    <span className="archive-project-title">{project.title}</span>
                  )}
                </span>
                <span className="archive-tech" role="cell">
                  {project.tech.map((t, i) => (
                    <TechPill key={i} tech={t} />
                  ))}
                </span>
                <span className="archive-link" role="cell">
                  {link && (
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      {link.label} <FiArrowUpRight className="title-arrow" />
                    </a>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="archive-gallery">
          {sortedProjects.map((project, index) => {
            const href = project.paperLink || project.external;
            const CardTag = href ? 'a' : 'div';
            const linkProps = href
              ? { href, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <CardTag className="archive-card" key={index} {...linkProps}>
                <div className="archive-card-image">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      width={400}
                      height={225}
                    />
                  ) : (
                    <div className="archive-card-placeholder">
                      <span>{project.title}</span>
                    </div>
                  )}
                  {href && (
                    <span className="archive-card-arrow">
                      <FiArrowUpRight />
                    </span>
                  )}
                </div>
                <h3 className="archive-card-title">{project.title}</h3>
                <span className="archive-card-year">{project.year}</span>
              </CardTag>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Archive;
